import axios, {AxiosResponse} from "axios";
import {PagedResult} from "@/service/PagedResult";
import Order from "@/model/Order";
import {OrderWorklogs} from "@/model/OrderWorklogs";
import {OrderType} from "@/enums";
import {OrderAssignmentRequest, OrderEmployeeAssignment} from "@/model/OrderEmployeeAssignment";

const PATH = "/v2/orders";

class OrderServiceV2 {

  getOrder(orderId: number): Promise<AxiosResponse<Order>> {
    return axios.get(`${PATH}/${orderId}`);
  }

  updateOrder(orderId: number, data: unknown) {
    return axios.patch(`${PATH}/${orderId}`, data, {headers: {"Content-Type": "application/merge-patch+json"}});
  }

  deleteOrder(orderId: number) {
    return axios.delete(`${PATH}/${orderId}`);
  }

  findOrdersInTimeframe(from: string, to: string, include: string | null = null, orderType: OrderType | null = null): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH}`, {
      params: {
        from: from,
        to: to,
        include: include,
        orderType: orderType,
        size: 1000
      }
    });
  }

  exportOrdersInTimeframe(from: string, to: string, orderType: OrderType | undefined = undefined): string {
    const baseUrl = `/api${PATH}/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const orderTypeParam = orderType ? `&orderType=${encodeURIComponent(orderType)}` : "";
    return baseUrl + orderTypeParam;
  }
   

  // Worklogs
  getOrderWorklogs(orderId: number, yearMonth: string | null): Promise<AxiosResponse<OrderWorklogs>> {
    if (yearMonth) {
      return axios.get(`${PATH}/${orderId}/worklogs`, {
        params: {
          yearMonth: yearMonth
        }
      });
    } else {
      return axios.get(`${PATH}/${orderId}/worklogs`);
    }
  }

  // Assignments, addressed by the order's and the employee's uuid
  getOrderAssignments(orderUuid: string): Promise<AxiosResponse<{ assignments: Array<OrderEmployeeAssignment> }>> {
    return axios.get(`${PATH}/${orderUuid}/assignments`);
  }

  updateOrderAssignment(orderUuid: string, employeeUuid: string, request: OrderAssignmentRequest): Promise<AxiosResponse<OrderEmployeeAssignment>> {
    return axios.put(`${PATH}/${orderUuid}/assignments/${employeeUuid}`, request);
  }

  deleteOrderAssignment(orderUuid: string, employeeUuid: string) {
    return axios.delete(`${PATH}/${orderUuid}/assignments/${employeeUuid}`);
  }

  // Per diems, addressed by the order's and the employees' uuids
  /**
   * Gives several employees the same per diems on one day, recorded for the order. Both amounts 0 without breakfast
   * deletes their per diems on that day instead. Applied to all employees or, if any of them is rejected, to none.
   */
  updateOrderPerDiems(orderUuid: string, request: OrderPerDiemsBulkRequest): Promise<AxiosResponse<void>> {
    return axios.post(`${PATH}/${orderUuid}/per-diems/_bulk`, request);
  }
}

/**
 * The per diems to give several employees of an order on one day.
 */
export interface OrderPerDiemsBulkRequest {
  date: string;
  employeeIds: Array<string>;
  catering: number;
  misc: number;
  breakfast: boolean;
}

export default new OrderServiceV2();
